import { Injectable } from '@angular/core';
import * as _ from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class TreeService {
    constructor() {}

    public getParents(item): any {
        let out = [];

        if (item && item.getParent && item.getParent()) {
            out.push(item.getParent());
            out = out.concat(this.getParents(item.getParent()));
        }

        return out;
    }

    public extendData(trunk, prop): any {
        const self = this;
        if (!prop) {
            prop = {};
        }
        if (!prop.parent) {
            prop.parent = null;
        }
        if (!prop.level) {
            prop.level = null;
        }

        _.forEach(trunk, branch => {
            branch.getParent = () => {
                return prop.parent;
            };
            branch.level = prop.level;

            _.forEach(self.getParents(branch).reverse(), parent => {
                branch[parent.level] = parent.value;
            });

            if (branch.level) {
                branch[branch.level] = branch.value;
            }

            if (_.isArray(branch.children)) {
                const newProp = { ...prop };
                newProp.parent = branch;
                this.extendData(branch.children, newProp);
            }
        });
    }

    public normalizeData(data): void {
        _.forEach(data, item => {
            delete item.getParent;
            delete item.level;
            delete item.checked;
            delete item.disabled;
            delete item.selected;
            delete item.indeterminate;
            delete item.children;
            delete item.hasChildren;
            delete item.value;
            delete item.label;
            delete item.expanded;
            delete item.refresh;
            delete item.showUp;
        });
    }

    public change(item, value): void {
        item.checked = value;
        this.eachChildrens(item.children, children => {
            children.checked = item.checked;
        });
    }

    public disable(item, value): void {
        item.disabled = value;
        this.eachChildrens(item.children, children => {
            children.disabled = item.disabled;
        });
    }

    public reloadAttrs(tree): void {
        const self = this;
        // each tree
        this.eachChildrens(
            tree,
            branch => {
                branch.indeterminate = false;
                branch.selected = branch.checked;

                if (
                    branch.hasChildren &&
                    _.isArray(branch.children) &&
                    branch.children.length > 0
                ) {
                    // is all checked childrens
                    if (self.isAllChildrenChecked(branch)) {
                        branch.selected = true;
                        branch.checked = true;
                    }
                    // if has some checked childrens
                    else if (
                        self.hasCheckedChild(branch) ||
                        self.hasIndeterminateChild(branch)
                    ) {
                        branch.indeterminate = true;
                        branch.checked = false;
                        branch.selected = false;
                    } // there is nothing checked, so the item should be unchecked too
                    else {
                        branch.indeterminate = false;
                        branch.checked = false;
                        branch.selected = false;
                    }
                }

                if (
                    branch.selected === true &&
                    self.hasSelectedParents(branch) === true
                ) {
                    branch.selected = false;
                }

                if (branch.selected === true) {
                    _.forEach(branch.children, b => {
                        b.selected = false;
                        b.indeterminate = false;
                        b.checked = true;
                        if (b.refresh) {
                            b.refresh();
                        }
                    });
                } else if (branch.indeterminate === true) {
                    _.forEach(branch.children, b => {
                        if (b.checked === true) {
                            b.selected = true;
                        }
                        if (b.refresh) {
                            b.refresh();
                        }
                    });
                }

                if (branch.refresh) {
                    branch.refresh();
                }
            },
            true
        );
    }

    public isAllChildrenCheckedWithFilter(item): boolean {
        if (!item || !item.children) {
            return true;
        }
        for (let i = 0; i < item.children.length; i++) {
            if (item.children[i].hasChildren) {
                if (!this.isAllChildrenCheckedWithFilter(item.children[i])) {
                    return false;
                }
            } else if (
                item.children[i].showUp === true &&
                !item.children[i].checked
            ) {
                return false;
            }
        }
        return true;
    }

    public isAllChildrenChecked(item): boolean {
        if (!item) {
            return false;
        }
        if (!item.children) {
            return false;
        }
        for (let i = 0; i < item.children.length; i++) {
            if (!item.children[i].checked) {
                return false;
            }
        }
        return true;
    }

    public isAllChildrenIncluded(item): boolean {
        if (!item) {
            return false;
        }
        if (!item.children) {
            return false;
        }
        for (let i = 0; i < item.children.length; i++) {
            if (!item.children[i].checked) {
                return false;
            }
        }
        return true;
    }

    public updateParent(parentItem): void {
        if (_.isEmpty(parentItem)) {
            return;
        }
        parentItem.selected = this.isAllChildrenIncluded(parentItem);
        this.updateParent(parentItem.getParent());
    }

    public hasCheckedChild(item): boolean {
        if (!item) {
            return false;
        }
        if (!item.children) {
            return false;
        }

        let i;
        for (i = 0; i < item.children.length; i++) {
            if (item.children[i].checked) {
                return true;
            }
        }

        return false;
    }

    public hasIndeterminateChild(item): boolean {
        if (!item) {
            return false;
        }
        if (!item.children) {
            return false;
        }

        let i;
        for (i = 0; i < item.children.length; i++) {
            if (item.children[i].indeterminate) {
                return true;
            }
        }

        return false;
    }

    public getSelected(tree): any {
        const out = [];

        this.eachChildrens(
            tree,
            item => {
                if (!item.disabled && item.selected) {
                    out.push(item);
                }
            },
            true
        );

        return out;
    }

    public getCheckedParents(tree): any {
        const out = [];
        const self = this;

        this.eachChildrens(
            tree,
            item => {
                if (
                    !item.disabled &&
                    item.checked &&
                    self.isParentIncluded(item)
                ) {
                    out.push(item);
                }
            },
            true
        );

        return out;
    }

    public setSelected(tree, treeLevels, vo, expandHandler): void {
        const attrs = [...treeLevels];
        const self = this;

        if (!attrs.length) {
            attrs.push('id');
        }

        _.forEach(vo, selectedItem => {
            const voTree = self.getAttrsObject(selectedItem, attrs);
            let found = false;

            this.eachChildrens(tree, item => {
                if (_.isEqual(voTree, self.getAttrsObject(item, attrs))) {
                    self.change(item, true);
                    found = true;
                }
            });

            if (!found && expandHandler) {
                const nearObject = self.getNearItem(
                    tree,
                    treeLevels,
                    selectedItem
                );
                if (nearObject) {
                    expandHandler(nearObject);
                }
            }
        });
    }

    public eachChildrens(branchs, f, byPass?): void {
        const self = this;
        _.forEach(branchs, (val, key) => {
            if (_.isArray(val.children)) {
                self.eachChildrens(val.children, f, byPass);
            }
            if (val.showUp === void 0 || byPass || val.showUp) {
                f(val, key);
            }
        });
    }

    public getTree(item, treeLevels): any {
        const out = [];

        _.forEach(item, (value, key) => {
            if (treeLevels && treeLevels.length) {
                _.forEach(treeLevels, level => {
                    if (key === level) {
                        out.push(value);
                    }
                });
            }
        });

        return out;
    }

    public getAttrsObject(item, attrs): any {
        const out = {};

        _.forEach(attrs, attr => {
            if (item[attr]) {
                out[attr] = item[attr];
            }
        });

        return out;
    }

    public loadSelection(
        tree,
        treeLevels,
        selection,
        expandHandler,
        customSettings?
    ): Promise<any> {
        const deferred: any = {};
        deferred.promise = new Promise(resolve => {
            deferred.resolve = resolve;
        });

        const selectionTree = this.getSelectionTree(
            selection,
            treeLevels,
            customSettings
        );
        this.setSelectedBySelectionTree(
            tree,
            selectionTree,
            expandHandler,
            deferred,
            customSettings
        );

        return deferred.promise;
    }

    public loadIncludeSection(
        tree,
        treeLevels,
        selection,
        expandHandler
    ): Promise<any> {
        const deferred: any = {};
        deferred.promise = new Promise(resolve => {
            deferred.resolve = resolve;
        });
        const selectionTree = this.getSelectionTree(selection, treeLevels);
        this.expandBySelectionTree(
            tree,
            selectionTree,
            expandHandler,
            deferred
        );
        return deferred.promise;
    }

    public toggleAllSelection(tree, selected): void {
        selected =
            selected === true || selected === false
                ? selected
                : this.isAllSelected(tree);

        this.eachChildrens(tree, item => {
            this.change(item, selected);
        });

        this.reloadAttrs(tree);
    }

    public isAllSelected(tree): boolean {
        if (!tree) {
            return false;
        }

        for (let i = 0; i < tree.length; i++) {
            if (
                tree[i] &&
                tree[i].hasOwnProperty('checked') &&
                !tree[i].checked
            ) {
                return false;
            }
        }

        return true;
    }

    private setSelectedBySelectionTree(
        tree,
        selectionTree,
        expandHandler,
        deferred,
        customSettings?
    ): void {
        const self = this;
        if (!selectionTree) {
            deferred.reject();
        }
        const promises = [];
        _.forEach(tree, treeItem => {
            if (!selectionTree.hasOwnProperty(treeItem.value)) {
                self.change(treeItem, false);
                return;
            }

            const deferred: any = {};
            deferred.promise = new Promise(resolve => {
                deferred.resolve = resolve;
            });

            if (selectionTree[treeItem.value].selected) {
                if (customSettings && customSettings.length) {
                    for (let i = 0; i < customSettings.length; i++) {
                        if (
                            selectionTree[treeItem.value].hasOwnProperty(
                                customSettings[i]
                            )
                        ) {
                            treeItem[customSettings[i]] =
                                selectionTree[treeItem.value][
                                    customSettings[i]
                                ];
                        }
                    }
                }
                self.change(treeItem, true);
                return;
            } else {
                self.change(treeItem, false);
            }

            const expandHandlerCallback = children => {
                self.setSelectedBySelectionTree(
                    children,
                    selectionTree[treeItem.value].toExpand,
                    expandHandler,
                    deferred
                );
            };
            expandHandler(treeItem, expandHandlerCallback);

            promises.push(deferred.promise);
        });

        Promise.all(promises).then(() => {
            deferred.resolve(tree);
        });
    }

    private expandBySelectionTree(
        tree,
        selectionTree,
        expandHandler,
        deferred
    ): void {
        const self = this;
        if (!selectionTree) {
            deferred.reject();
        }
        const promises = [];
        _.forEach(tree, treeItem => {
            if (!selectionTree.hasOwnProperty(treeItem.value)) {
                return;
            }
            const deferred: any = {};
            deferred.promise = new Promise(resolve => {
                deferred.resolve = resolve;
            });
            const expandHandlerCallback = children => {
                self.expandBySelectionTree(
                    children,
                    selectionTree[treeItem.value].toExpand,
                    expandHandler,
                    deferred
                );
            };
            expandHandler(treeItem, expandHandlerCallback);
            promises.push(deferred.promise);
        });
        Promise.all(promises).then(() => {
            deferred.resolve(tree);
        });
    }

    private getSelectionTree(
        plainItems,
        treeLevels,
        customSettings?: any
    ): any {
        if (!plainItems || !plainItems.length) {
            return {};
        }
        if (!treeLevels || !treeLevels.length) {
            return {};
        }

        const result = {
            // level: 'root',
            toExpand: {},
            selected: false
        };

        let levelIndex;

        _.forEach(plainItems, item => {
            let itemLevel = null; // eslint-disable-line no-unused-lets
            let parentLevel = result;

            // here we assume that there are no gaps amoong tree level
            // an item always should have all their parent values
            for (levelIndex = 0; levelIndex < treeLevels.length; levelIndex++) {
                const levelValue = item[treeLevels[levelIndex]];
                if (levelValue) {
                    itemLevel = treeLevels[levelIndex];

                    if (!parentLevel.toExpand.hasOwnProperty(levelValue)) {
                        parentLevel.toExpand[levelValue] = {
                            // level: treeLevels[levelIndex],
                            toExpand: {},
                            selected: false
                        };
                        if (customSettings && customSettings.length) {
                            for (let i = 0; i < customSettings.length; i++) {
                                if (item.hasOwnProperty(customSettings[i])) {
                                    parentLevel.toExpand[levelValue][
                                        customSettings[i]
                                    ] = item[customSettings[i]];
                                }
                            }
                        }
                    }

                    parentLevel = parentLevel.toExpand[levelValue];
                } else {
                    break;
                }
            }

            parentLevel.selected = true;
        });

        return result.toExpand;
    }

    private hasSelectedParents(item): boolean {
        let parent = item.getParent();

        while (parent) {
            // exit if at least one parent is selected
            if (parent.selected) {
                return true;
            }

            parent = parent.getParent();
        }

        return false;
    }

    private isParentIncluded(item): boolean {
        const parent = item.getParent();
        if (
            (_.isObject(parent) && parent.indeterminate) ||
            !_.isObject(parent)
        ) {
            return true;
        }
        return false;
    }

    private getNearItem(tree, treeLevels, item): any {
        let out = null;
        const data = [];

        this.eachChildrens(tree, branch => {
            let matches = 0;

            if (treeLevels && treeLevels.length) {
                _.forEach(treeLevels, level => {
                    if (
                        item[level] !== undefined &&
                        branch[level] !== undefined &&
                        item[level] === branch[level]
                    ) {
                        matches++;
                    }
                });
            }

            if (matches) {
                data[matches] = branch;
            }
        });

        _.forEach(data, node => {
            out = node;
        });

        return out;
    }
}
